// Phase 2 (PLAN_DRAWING_SSOT_MIGRATION_2026-09-27): these 22 symbols used to be
// imported from the "@vninvestcharts/engine-drawing" barrel. Inside the package
// that is a self-import and does not resolve, and the 15 target modules are
// byte-identical in both repos, so they are referenced relatively instead.
import { chartPointToPixel } from "./coordinateUtils";
import { SEGMENT_KEYS_BY_TYPE, isSegmentVisible, resolveSegmentStyle } from "./segmentRegistry";
import { calculateAbcdPatternMetrics } from "./builtin/abcdPattern";
import { calculateCycleGeometry } from "./builtin/cycles";
import { calculateElliottWaveGeometry } from "./builtin/elliottWaves";
import { calculateFibArcGeometry } from "./builtin/fibArc";
import { FIB_EXTENDED_LEVELS, FIB_FAN_RATIOS, calculateFibExtendedGeometry } from "./builtin/fibExtended";
import { calculateFibTimeZoneGeometry } from "./builtin/fibTimeZone";
import { GANN_FRACTIONS, calculateGannAdvancedGeometry } from "./builtin/gannAdvanced";
import { calculateGannFanGeometry } from "./builtin/gannFan";
import { calculateHarmonicPatternGeometry } from "./builtin/harmonicPatterns";
import { calculateParallelChannelGeometry } from "./builtin/parallelChannel";
import { calculatePitchforkGeometry } from "./builtin/pitchfork";
import { calculateRegressionChannelMetrics } from "./builtin/regressionChannel";
import { buildAnnotationPrimitive } from "./adapters/annotationPrimitives";
import { calculateDisjointChannelGeometry } from "./builtin/disjointChannel";
import { calculateFlatTopBottomGeometry } from "./builtin/flatTopBottom";
import { calculateSchiffPitchforkGeometry } from "./builtin/schiffPitchfork";
import { calculateModifiedSchiffGeometry } from "./builtin/modifiedSchiff";
import { calculateInsidePitchforkGeometry } from "./builtin/insidePitchfork";
import { calculateGannFixedGeometry } from "./builtin/gannFixed";
import { calculateTrendAngleGeometry } from "./builtin/trendAngle";
import { calculateInfoLineGeometry } from "./builtin/infoLine";
import { calculateAnchoredVwapGeometry } from "./builtin/anchoredVwap";
import { computeHarmonicRatioLabels, formatHarmonicRatioLabel } from "./harmonicRatioValidation";
const _primitiveBuilders = new Map();
export function registerPrimitiveBuilder(type, builder) {
    if (_primitiveBuilders.has(type)) {
        console.warn(`[drawing] registerPrimitiveBuilder: type "${type}" already registered; ignoring duplicate.`);
        return;
    }
    _primitiveBuilders.set(type, builder);
}
export function unregisterPrimitiveBuilder(type) {
    return _primitiveBuilders.delete(type);
}
export function hasPrimitiveBuilder(type) {
    return _primitiveBuilders.has(type);
}
const DEFAULT_FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1, 1.618, 2.618];
const DEFAULT_FIB_EXTENSION_LEVELS = [1.272, 1.414, 1.618, 2, 2.618];
const TREND_BASED_FIB_TIME_LEVELS = [0, 1, 1, 2, 3, 5, 8, 13, 21];
function toPixel(point, scales) {
    return chartPointToPixel(point, scales);
}
function isValidPixel(point) {
    return Number.isFinite(point.x) && Number.isFinite(point.y);
}
function clipSegmentToViewport(seg, chartWidth, chartHeight) {
    // Both endpoints NaN → skip entirely
    if (!isValidPixel(seg.start) && !isValidPixel(seg.end)) {
        return null;
    }
    // Both endpoints finite → clip to viewport
    if (isValidPixel(seg.start) && isValidPixel(seg.end)) {
        const clipped = clipSegmentToBox(seg.start, seg.end, chartWidth, chartHeight);
        return clipped ? { start: clipped[0], end: clipped[1] } : null;
    }
    // Exactly one endpoint NaN → keep the visible endpoint, replace NaN with viewport edge
    const visiblePoint = isValidPixel(seg.start) ? seg.start : seg.end;
    const box = { x: 0, y: 0, width: chartWidth, height: chartHeight };
    // Extend from visible point in all four directions to find viewport intersection
    const directions = [
        { x: 1, y: 0 }, { x: -1, y: 0 }, { x: 0, y: 1 }, { x: 0, y: -1 },
    ];
    for (const dir of directions) {
        const extended = clipSegmentToBox({ x: visiblePoint.x - dir.x * 10000, y: visiblePoint.y - dir.y * 10000 }, { x: visiblePoint.x + dir.x * 10000, y: visiblePoint.y + dir.y * 10000 }, chartWidth, chartHeight);
        if (extended) {
            // Pick the endpoint that's NOT the visible point (or closest to the visible point if both are visible)
            const [a, b] = extended;
            const distA = Math.hypot(a.x - visiblePoint.x, a.y - visiblePoint.y);
            const distB = Math.hypot(b.x - visiblePoint.x, b.y - visiblePoint.y);
            const farPoint = distA > distB ? a : b;
            // The far point might be the visible point itself if the line is very short
            if (Math.hypot(visiblePoint.x - farPoint.x, visiblePoint.y - farPoint.y) > 1) {
                return { start: visiblePoint, end: farPoint };
            }
        }
    }
    // Cannot extend to viewport — keep as-is (clipToPane will handle)
    return seg;
}
function toPrimitiveStyle(style) {
    return {
        stroke: style.stroke,
        strokeWidth: style.strokeWidth,
        strokeDasharray: style.strokeDasharray,
        fill: style.fill,
        fillOpacity: style.fillOpacity,
        opacity: style.opacity,
        fontSize: style.fontSize,
        fontFamily: style.fontFamily,
        textBoxWidth: style.textBoxWidth,
        textBoxHeight: style.textBoxHeight,
        backgroundColor: style.backgroundColor,
        borderColor: style.borderColor,
        borderRadius: style.borderRadius,
        shadowBlur: style.shadowBlur,
        bold: style.bold,
        italic: style.italic,
        textAlign: style.textAlign,
    };
}
function clipSegmentToBox(start, end, chartWidth, chartHeight) {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    let t0 = 0;
    let t1 = 1;
    const edges = [
        [-dx, start.x],
        [dx, chartWidth - start.x],
        [-dy, start.y],
        [dy, chartHeight - start.y],
    ];
    for (const [p, q] of edges) {
        if (p === 0) {
            if (q < 0) {
                return null;
            }
            continue;
        }
        const r = q / p;
        if (p < 0) {
            if (r > t1) {
                return null;
            }
            if (r > t0) {
                t0 = r;
            }
        }
        else {
            if (r < t0) {
                return null;
            }
            if (r < t1) {
                t1 = r;
            }
        }
    }
    if (t1 < t0) {
        return null;
    }
    return [
        { x: start.x + dx * t0, y: start.y + dy * t0 },
        { x: start.x + dx * t1, y: start.y + dy * t1 },
    ];
}
function extendLineThroughBox(point, direction, chartWidth, chartHeight) {
    return clipSegmentToBox({ x: point.x - direction.x * 10_000, y: point.y - direction.y * 10_000 }, { x: point.x + direction.x * 10_000, y: point.y + direction.y * 10_000 }, chartWidth, chartHeight);
}
function toPrimitiveSegment(drawing, key, start, end, patch) {
    const style = toPrimitiveStyle(resolveSegmentStyle(drawing, key));
    return {
        start,
        end,
        color: style.stroke,
        strokeWidth: style.strokeWidth,
        strokeDasharray: patch?.strokeDasharray ?? style.strokeDasharray,
        dashPattern: patch?.dashPattern,
        opacity: patch?.opacity,
        hitTestable: patch?.hitTestable,
        label: patch?.label,
        labelPosition: patch?.labelPosition,
    };
}
function buildAbcdPatternPrimitive(drawing, scales, primitiveStyle) {
    const metrics = calculateAbcdPatternMetrics(drawing, scales);
    if (!metrics) {
        return null;
    }
    const labels = drawing.waveLabels ?? ["A", "B", "C", "D"];
    const fontSize = drawing.style.fontSize ?? 11;
    const points = metrics.points;
    const segments = [];
    const segmentKeys = SEGMENT_KEYS_BY_TYPE.abcdPattern ?? [];
    for (let index = 0; index < segmentKeys.length && index + 1 < points.length; index += 1) {
        const key = segmentKeys[index];
        if (!isSegmentVisible(drawing, key)) {
            continue;
        }
        segments.push(toPrimitiveSegment(drawing, key, points[index], points[index + 1]));
    }
    const pointLabels = points.map((point, index) => ({
        text: labels[index] ?? ["A", "B", "C", "D"][index] ?? "",
        position: { x: point.x + 6, y: point.y - 6 },
        baseline: "alphabetic",
        weight: 700,
        fontSize,
    })).filter((label) => label.text.length > 0);
    const ratioLabels = computeHarmonicRatioLabels(drawing);
    const valueLabels = ratioLabels.length >= 2
        ? [
            {
                text: formatHarmonicRatioLabel(ratioLabels[0]),
                position: { x: metrics.segmentMidpoints[1].x, y: metrics.segmentMidpoints[1].y - 10 },
                align: "center",
                baseline: "alphabetic",
                weight: 700,
                fontSize,
                color: ratioLabels[0].color,
            },
            {
                text: formatHarmonicRatioLabel(ratioLabels[1]),
                position: { x: metrics.segmentMidpoints[2].x, y: metrics.segmentMidpoints[2].y - 10 },
                align: "center",
                baseline: "alphabetic",
                weight: 700,
                fontSize,
                color: ratioLabels[1].color,
            },
        ]
        : [
            {
                text: metrics.bcToAb == null ? "BC/AB n/a" : `BC/AB ${metrics.bcToAb.toFixed(2)}`,
                position: { x: metrics.segmentMidpoints[1].x, y: metrics.segmentMidpoints[1].y - 10 },
                align: "center",
                baseline: "alphabetic",
                weight: 700,
                fontSize,
            },
            {
                text: metrics.cdToBc == null ? "CD/BC n/a" : `CD/BC ${metrics.cdToBc.toFixed(2)}`,
                position: { x: metrics.segmentMidpoints[2].x, y: metrics.segmentMidpoints[2].y - 10 },
                align: "center",
                baseline: "alphabetic",
                weight: 700,
                fontSize,
            },
        ];
    return {
        type: "abcdPattern",
        points,
        style: primitiveStyle,
        segments,
        labels: [...pointLabels, ...valueLabels],
    };
}
function buildHarmonicPatternPrimitive(drawing, scales, primitiveStyle, options) {
    const geometry = calculateHarmonicPatternGeometry(drawing, scales);
    if (!geometry || geometry.pixels.length < 2) {
        return null;
    }
    const { pixels, labels, hasClosingLine, isHeadAndShoulders } = geometry;
    const fontSize = drawing.style.fontSize ?? 11;
    const segmentKeys = SEGMENT_KEYS_BY_TYPE[drawing.type] ?? [];
    const segments = [];
    const seqKeys = segmentKeys.length > 0 && hasClosingLine ? segmentKeys.slice(0, segmentKeys.length - 1) : segmentKeys;
    if (seqKeys.length > 0) {
        for (let index = 0; index < seqKeys.length && index + 1 < pixels.length; index += 1) {
            const key = seqKeys[index];
            if (!isSegmentVisible(drawing, key)) {
                continue;
            }
            segments.push(toPrimitiveSegment(drawing, key, pixels[index], pixels[index + 1]));
        }
    }
    else {
        for (let index = 1; index < pixels.length; index += 1) {
            segments.push({
                start: pixels[index - 1],
                end: pixels[index],
                color: primitiveStyle.stroke,
                strokeWidth: primitiveStyle.strokeWidth,
                strokeDasharray: primitiveStyle.strokeDasharray,
            });
        }
    }
    if (hasClosingLine && pixels.length >= 2) {
        const first = pixels[0];
        const last = pixels[pixels.length - 1];
        const closingKey = segmentKeys.length > 0 ? segmentKeys[segmentKeys.length - 1] : undefined;
        if (!closingKey || isSegmentVisible(drawing, closingKey)) {
            segments.push(closingKey
                ? toPrimitiveSegment(drawing, closingKey, first, last, { strokeDasharray: "dashed", dashPattern: [5, 3], hitTestable: false })
                : {
                    start: first,
                    end: last,
                    color: primitiveStyle.stroke,
                    strokeWidth: primitiveStyle.strokeWidth,
                    strokeDasharray: "dashed",
                    dashPattern: [5, 3],
                    hitTestable: false,
                });
        }
    }
    if (isHeadAndShoulders && pixels.length >= 7) {
        const leftNeckline = pixels[5];
        const rightNeckline = pixels[6];
        const dx = rightNeckline.x - leftNeckline.x;
        const dy = rightNeckline.y - leftNeckline.y;
        const extension = 0.2;
        segments.push({
            start: { x: leftNeckline.x - dx * extension, y: leftNeckline.y - dy * extension },
            end: { x: rightNeckline.x + dx * extension, y: rightNeckline.y + dy * extension },
            color: "#faad14",
            strokeWidth: primitiveStyle.strokeWidth,
            strokeDasharray: "dashed",
            dashPattern: [6, 3],
            hitTestable: false,
        });
    }
    const ratioLabels = computeHarmonicRatioLabels(drawing).map((label, index) => {
        const start = pixels[index];
        const end = pixels[index + 1];
        if (!start || !end) {
            return null;
        }
        return {
            text: formatHarmonicRatioLabel(label),
            position: { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 - 10 },
            align: "center",
            baseline: "alphabetic",
            weight: 700,
            fontSize,
            color: label.color,
        };
    }).filter((label) => label !== null);
    return {
        type: drawing.type,
        points: pixels,
        style: primitiveStyle,
        polygons: drawing.style.fill && drawing.style.fill !== "none"
            ? [{ points: pixels, fill: drawing.style.fill, opacity: (drawing.style.fillOpacity ?? 0.1) * (drawing.style.opacity ?? 1) }]
            : undefined,
        segments,
        labels: ratioLabels,
        semanticBadges: labels.map((label, index) => ({
            label,
            position: pixels[index],
            isFocused: options?.focusedPointIndex === index,
        })),
    };
}
function buildElliottWavePrimitive(drawing, scales, primitiveStyle, options) {
    const geometry = calculateElliottWaveGeometry(drawing, scales);
    if (!geometry || geometry.pixels.length < 2) {
        return null;
    }
    const { pixels, labels } = geometry;
    const segmentKeys = SEGMENT_KEYS_BY_TYPE[drawing.type] ?? [];
    const segments = [];
    if (segmentKeys.length > 0) {
        for (let index = 0; index < segmentKeys.length && index + 1 < pixels.length; index += 1) {
            const key = segmentKeys[index];
            if (!isSegmentVisible(drawing, key)) {
                continue;
            }
            segments.push(toPrimitiveSegment(drawing, key, pixels[index], pixels[index + 1]));
        }
    }
    else {
        for (let index = 1; index < pixels.length; index += 1) {
            segments.push({
                start: pixels[index - 1],
                end: pixels[index],
                color: primitiveStyle.stroke,
                strokeWidth: primitiveStyle.strokeWidth,
                strokeDasharray: primitiveStyle.strokeDasharray,
            });
        }
    }
    return {
        type: drawing.type,
        points: pixels,
        style: primitiveStyle,
        segments,
        semanticBadges: labels.map((label, index) => ({
            label,
            position: pixels[index],
            isFocused: options?.focusedPointIndex === index,
        })),
    };
}
function buildGannFanPrimitive(drawing, scales, primitiveStyle, options) {
    const [originPoint, endPoint] = drawing.points;
    if (!originPoint || !endPoint || options?.chartWidth == null || options?.chartHeight == null) {
        return null;
    }
    const origin = toPixel(originPoint, scales);
    const end = toPixel(endPoint, scales);
    const geometry = calculateGannFanGeometry(origin.x, origin.y, end.x, end.y, options.chartWidth, options.chartHeight);
    const segmentKeys = SEGMENT_KEYS_BY_TYPE.gannFan ?? [];
    const segments = [];
    const labels = [];
    for (let index = 0; index < geometry.lines.length; index += 1) {
        const line = geometry.lines[index];
        const key = segmentKeys[index];
        if (key && !isSegmentVisible(drawing, key)) {
            continue;
        }
        const segmentStyle = key ? resolveSegmentStyle(drawing, key) : drawing.style;
        segments.push({
            start: { x: line.x1, y: line.y1 },
            end: { x: line.x2, y: line.y2 },
            color: segmentStyle.stroke,
            strokeWidth: line.isPrincipal ? segmentStyle.strokeWidth + 1 : segmentStyle.strokeWidth,
            strokeDasharray: "solid",
        });
        labels.push({
            text: line.label,
            position: {
                x: Math.max(4, Math.min(options.chartWidth - 30, line.x2 + 4)),
                y: Math.max(12, Math.min(options.chartHeight - 4, line.y2 - 4)),
            },
            color: segmentStyle.stroke,
            align: "left",
            baseline: "alphabetic",
            weight: line.isPrincipal ? 700 : 400,
            fontSize: segmentStyle.fontSize ?? 10,
        });
    }
    return {
        type: "gannFan",
        points: [origin, end],
        style: primitiveStyle,
        segments,
        labels,
    };
}
function buildGannAdvancedPrimitive(drawing, scales, primitiveStyle) {
    const geometry = calculateGannAdvancedGeometry(drawing, scales);
    if (!geometry) {
        return null;
    }
    const { x1, y1, x2, y2 } = geometry;
    const left = Math.min(x1, x2);
    const right = Math.max(x1, x2);
    const top = Math.min(y1, y2);
    const bottom = Math.max(y1, y2);
    const width = right - left;
    const height = bottom - top;
    const baseOpacity = primitiveStyle.opacity ?? 1;
    const segments = [
        { start: { x: left, y: top }, end: { x: right, y: top }, color: primitiveStyle.stroke, strokeWidth: primitiveStyle.strokeWidth },
        { start: { x: right, y: top }, end: { x: right, y: bottom }, color: primitiveStyle.stroke, strokeWidth: primitiveStyle.strokeWidth },
        { start: { x: right, y: bottom }, end: { x: left, y: bottom }, color: primitiveStyle.stroke, strokeWidth: primitiveStyle.strokeWidth },
        { start: { x: left, y: bottom }, end: { x: left, y: top }, color: primitiveStyle.stroke, strokeWidth: primitiveStyle.strokeWidth },
    ];
    for (const fraction of GANN_FRACTIONS) {
        segments.push({
            start: { x: left, y: top + height * fraction },
            end: { x: right, y: top + height * fraction },
            color: primitiveStyle.stroke,
            strokeWidth: 1,
            opacity: 0.4 * baseOpacity,
            hitTestable: false,
        }, {
            start: { x: left + width * fraction, y: top },
            end: { x: left + width * fraction, y: bottom },
            color: primitiveStyle.stroke,
            strokeWidth: 1,
            opacity: 0.4 * baseOpacity,
            hitTestable: false,
        });
    }
    segments.push({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, color: "#ff4d4f", strokeWidth: primitiveStyle.strokeWidth, hitTestable: false }, { start: { x: x1, y: y2 }, end: { x: x2, y: y1 }, color: primitiveStyle.stroke, strokeWidth: 1, opacity: 0.4 / baseOpacity, hitTestable: false });
    return {
        type: drawing.type,
        points: [{ x: x1, y: y1 }, { x: x2, y: y2 }],
        style: primitiveStyle,
        segments,
    };
}
function buildPositionPrimitive(drawing, scales, primitiveStyle, mode) {
    const [startPoint, endPoint] = drawing.points;
    if (!startPoint || !endPoint) {
        return null;
    }
    const start = toPixel(startPoint, scales);
    const end = toPixel(endPoint, scales);
    const left = Math.min(start.x, end.x);
    const right = Math.max(start.x, end.x);
    const riskReward = drawing.riskReward ?? {
        entry: startPoint.y,
        stop: mode === "long" ? Math.min(startPoint.y, endPoint.y) : Math.max(startPoint.y, endPoint.y),
        target: mode === "long" ? Math.max(startPoint.y, endPoint.y) : Math.min(startPoint.y, endPoint.y),
    };
    const entryY = toPixel({ ...startPoint, y: riskReward.entry, yPrice: riskReward.entry }, scales).y;
    const stopY = toPixel({ ...startPoint, y: riskReward.stop, yPrice: riskReward.stop }, scales).y;
    const targetY = toPixel({ ...startPoint, y: riskReward.target, yPrice: riskReward.target }, scales).y;
    const reward = Math.abs(riskReward.target - riskReward.entry);
    const risk = Math.abs(riskReward.entry - riskReward.stop);
    const ratio = risk > 0 ? reward / risk : undefined;
    const badge = ratio ? `R/R 1:${ratio.toFixed(1)}` : "R/R n/a";
    return {
        type: drawing.type,
        points: [start, end],
        style: primitiveStyle,
        polygons: [
            {
                points: [
                    { x: left, y: Math.min(entryY, targetY) },
                    { x: right, y: Math.min(entryY, targetY) },
                    { x: right, y: Math.max(entryY, targetY) },
                    { x: left, y: Math.max(entryY, targetY) },
                ],
                fill: "rgba(34, 171, 92, 0.18)",
            },
            {
                points: [
                    { x: left, y: Math.min(entryY, stopY) },
                    { x: right, y: Math.min(entryY, stopY) },
                    { x: right, y: Math.max(entryY, stopY) },
                    { x: left, y: Math.max(entryY, stopY) },
                ],
                fill: "rgba(215, 50, 75, 0.18)",
            },
        ],
        segments: [
            { start: { x: left, y: entryY }, end: { x: right, y: entryY } },
            { start: { x: left, y: targetY }, end: { x: right, y: targetY } },
            { start: { x: left, y: stopY }, end: { x: right, y: stopY } },
        ],
        labels: [{
                text: badge,
                position: { x: right - 4, y: Math.min(entryY, stopY, targetY) - 6 },
                align: "right",
                baseline: "alphabetic",
                weight: 700,
                fontSize: drawing.style.fontSize ?? 11,
            }],
    };
}
function buildCyclePrimitive(drawing, scales, primitiveStyle) {
    const geometry = calculateCycleGeometry(drawing, scales);
    if (!geometry) {
        return null;
    }
    const { type, x1, y1, x2, y2, amplitude } = geometry;
    const count = Math.max(1, geometry.count);
    const anchorSegment = {
        start: { x: x1, y: y1 },
        end: { x: x2, y: y2 },
        opacity: 0,
    };
    if (type === "cyclicLines") {
        const dx = (x2 - x1) / count;
        const top = Math.min(y1, y2) - 20;
        const bottom = Math.max(y1, y2) + 20;
        return {
            type,
            points: [{ x: x1, y: y1 }, { x: x2, y: y2 }],
            style: primitiveStyle,
            segments: [
                anchorSegment,
                ...Array.from({ length: count + 1 }, (_, index) => {
                    const x = x1 + index * dx;
                    return {
                        start: { x, y: top },
                        end: { x, y: bottom },
                        opacity: index % 2 === 0 ? 0.8 : 0.4,
                        hitTestable: false,
                    };
                }),
            ],
        };
    }
    if (type === "timeCycles") {
        const dx = (x2 - x1) / count;
        const radius = Math.abs(dx) / 2;
        const paths = Array.from({ length: count }, (_, index) => {
            const centerX = x1 + (index + 0.5) * dx;
            return {
                points: Array.from({ length: 25 }, (_, step) => {
                    const theta = (step / 24) * Math.PI;
                    return {
                        x: centerX + radius * Math.cos(theta),
                        y: y1 + radius * Math.sin(theta),
                    };
                }),
            };
        });
        return {
            type,
            points: [{ x: x1, y: y1 }, { x: x2, y: y2 }],
            style: primitiveStyle,
            segments: [anchorSegment],
            paths,
        };
    }
    const totalWidth = x2 - x1;
    const halfPeriod = totalWidth / count || Number.EPSILON;
    const steps = Math.max(200, Math.round(Math.abs(totalWidth) * 2));
    return {
        type,
        points: [{ x: x1, y: y1 }, { x: x2, y: y2 }],
        style: primitiveStyle,
        segments: [anchorSegment],
        paths: [{
                points: Array.from({ length: steps + 1 }, (_, index) => {
                    const px = x1 + (index / steps) * totalWidth;
                    return {
                        x: px,
                        y: y1 - amplitude * Math.sin(Math.PI * (px - x1) / halfPeriod),
                    };
                }),
            }],
    };
}
function buildDateAndPriceRangeBadge(drawing, scales) {
    const [startPoint, endPoint] = drawing.points;
    if (!startPoint || !endPoint) {
        return undefined;
    }
    const start = toPixel(startPoint, scales);
    const end = toPixel(endPoint, scales);
    const percent = startPoint.y === 0 ? 0 : ((endPoint.y - startPoint.y) / Math.abs(startPoint.y)) * 100;
    const bars = Math.max(1, Math.round(Math.abs(end.x - start.x) / 6));
    return `Δ ${percent >= 0 ? "+" : ""}${percent.toFixed(2)}% · ${bars} bars`;
}
function formatOverlayNumber(value) {
    return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
}
function buildFibonacciLevelLines(drawing, scales, mode) {
    const [startPoint, endPoint] = drawing.points;
    if (!startPoint || !endPoint) {
        return undefined;
    }
    const start = toPixel(startPoint, scales);
    const end = toPixel(endPoint, scales);
    const priceDelta = endPoint.y - startPoint.y;
    const pixelDelta = end.y - start.y;
    const fallbackLevels = mode === "extension" ? DEFAULT_FIB_EXTENSION_LEVELS : DEFAULT_FIB_LEVELS;
    if (drawing.fibLevelConfigs && drawing.fibLevelConfigs.length > 0) {
        return drawing.fibLevelConfigs
            .filter((config) => config.visible)
            .map((config) => {
            const price = mode === "extension" ? endPoint.y + priceDelta * config.ratio : startPoint.y + priceDelta * config.ratio;
            const y = mode === "extension" ? end.y + pixelDelta * config.ratio : start.y + pixelDelta * config.ratio;
            return {
                y,
                label: `${(config.ratio * 100).toFixed(1)}% — ${formatOverlayNumber(price)}`,
                color: config.color,
                yPrice: price,
            };
        });
    }
    return (drawing.fibLevels ?? fallbackLevels).map((ratio) => {
        const price = mode === "extension" ? endPoint.y + priceDelta * ratio : startPoint.y + priceDelta * ratio;
        const y = mode === "extension" ? end.y + pixelDelta * ratio : start.y + pixelDelta * ratio;
        return {
            y,
            label: `${(ratio * 100).toFixed(1)}% — ${formatOverlayNumber(price)}`,
            yPrice: price,
        };
    });
}
function buildFibArcLevels(drawing, scales) {
    const geometry = calculateFibArcGeometry(drawing, scales);
    if (!geometry) {
        return undefined;
    }
    return geometry.radii.map((radius, index) => ({
        radius,
        label: `${(geometry.levels[index] * 100).toFixed(1)}%`,
        radiusRatio: geometry.levels[index],
    }));
}
function buildFibTimeZoneMarkers(drawing, scales) {
    const geometry = calculateFibTimeZoneGeometry(drawing, scales);
    if (!geometry) {
        return undefined;
    }
    const [startPoint, endPoint] = drawing.points;
    const startIndex = startPoint?.xIndexFloat;
    const endIndex = endPoint?.xIndexFloat;
    return geometry.positions.map((x, index) => {
        let xIndexFloat;
        if (typeof startIndex === "number" && typeof endIndex === "number") {
            const stepIndex = endIndex - startIndex;
            xIndexFloat = startIndex + stepIndex * geometry.levels[index];
        }
        return {
            x,
            label: `${geometry.levels[index]}`,
            xIndexFloat,
        };
    });
}
function buildFibExtendedPrimitive(drawing, scales, primitiveStyle) {
    const geometry = calculateFibExtendedGeometry(drawing, scales);
    if (!geometry) {
        return null;
    }
    const { type, p1, p2, p3 } = geometry;
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const baseRadius = Math.hypot(dx, dy);
    switch (type) {
        case "fibCircles":
        case "fibSpeedResistanceArcs":
            return {
                type,
                points: [p1, p2],
                style: primitiveStyle,
                arcLevels: FIB_EXTENDED_LEVELS.map((level) => ({
                    radius: baseRadius * level,
                    label: String(level),
                    labelPosition: { x: p1.x + baseRadius * level * 0.707, y: p1.y - baseRadius * level * 0.707 },
                    startAngle: type === "fibSpeedResistanceArcs" ? Math.PI : 0,
                    endAngle: Math.PI * 2,
                    opacity: 0.7,
                    radiusRatio: level,
                })),
                pointMarkers: [{ point: p1, radius: 3 }],
            };
        case "fibSpiral": {
            const phi = 1.6180339887;
            const steps = 200;
            const turns = 4 * Math.PI;
            const angle0 = Math.atan2(dy, dx);
            const points = Array.from({ length: steps + 1 }, (_, index) => {
                const theta = (index / steps) * turns;
                const radius = Math.pow(phi, theta / (Math.PI / 2));
                return {
                    x: p1.x + radius * Math.cos(theta + angle0),
                    y: p1.y + radius * Math.sin(theta + angle0),
                };
            });
            return {
                type,
                points: [p1, p2],
                style: primitiveStyle,
                paths: [{ points }],
            };
        }
        case "fibSpeedResistanceFan":
            return {
                type,
                points: [p1, p2],
                style: primitiveStyle,
                segments: FIB_FAN_RATIOS.map((ratio) => {
                    const targetY = p1.y - (p1.y - p2.y) * ratio;
                    return {
                        start: p1,
                        end: { x: p2.x, y: targetY },
                        label: String(ratio),
                        labelPosition: { x: p2.x + 4, y: targetY },
                        opacity: ratio === 0.618 ? 1 : 0.7,
                    };
                }),
            };
        case "fibChannel": {
            const offsetY = p3 ? p3.y - p1.y : p2.y - p1.y;
            return {
                type,
                points: p3 ? [p1, p2, p3] : [p1, p2],
                style: primitiveStyle,
                segments: FIB_EXTENDED_LEVELS.slice(0, 7).map((level) => {
                    const lineY1 = p1.y + offsetY * level;
                    const lineY2 = p2.y + offsetY * level;
                    return {
                        start: { x: p1.x, y: lineY1 },
                        end: { x: p2.x, y: lineY2 },
                        label: String(level),
                        labelPosition: { x: p2.x + 4, y: lineY2 },
                        opacity: level === 0.618 ? 1 : 0.6,
                    };
                }),
            };
        }
        case "trendBasedFibTime": {
            const period = p2.x - p1.x;
            return {
                type,
                points: p3 ? [p1, p2, p3] : [p1, p2],
                style: primitiveStyle,
                segments: TREND_BASED_FIB_TIME_LEVELS.map((level) => {
                    const x = p1.x + period * level;
                    return {
                        start: { x, y: 0 },
                        end: { x, y: 2000 },
                        label: String(level),
                        labelPosition: { x: x + 3, y: p1.y - 8 },
                        opacity: 0.6,
                    };
                }),
            };
        }
        case "fibWedge": {
            const apex = p3 ?? p2;
            const retracementLevels = [0.382, 0.5, 0.618].map((level) => ({
                start: { x: p1.x + (p2.x - p1.x) * level, y: p1.y + (p2.y - p1.y) * level },
                end: apex,
                strokeDasharray: "dashed",
                opacity: 0.5,
            }));
            return {
                type,
                points: p3 ? [p1, p2, p3] : [p1, p2],
                style: primitiveStyle,
                segments: [
                    { start: p1, end: apex },
                    { start: p2, end: apex },
                    ...retracementLevels,
                ],
            };
        }
        case "pitchfan": {
            const fanSegments = FIB_FAN_RATIOS.map((ratio) => {
                const tx = p3 ? p2.x + (p3.x - p2.x) * ratio : p2.x;
                const ty = p3 ? p2.y + (p3.y - p2.y) * ratio : p2.y;
                const extX = p1.x + (tx - p1.x) * 3;
                const extY = p1.y + (ty - p1.y) * 3;
                return {
                    start: p1,
                    end: { x: extX, y: extY },
                    label: String(ratio),
                    labelPosition: { x: extX - 20, y: extY },
                    opacity: ratio === 0.5 ? 1 : 0.65,
                };
            });
            return {
                type,
                points: p3 ? [p1, p2, p3] : [p1, p2],
                style: primitiveStyle,
                segments: p3
                    ? [...fanSegments, { start: p2, end: p3, strokeDasharray: "dashed" }]
                    : fanSegments,
            };
        }
        default:
            if (process.env.NODE_ENV !== "production") {
                // eslint-disable-next-line no-console
                console.warn("[drawing] unhandled DrawingToolType in toOverlayPrimitiveRaw:", drawing.type);
            }
            return null;
    }
}
function buildChannelPrimitive(drawing, scales, primitiveStyle) {
    const [startPoint, endPoint, offsetPoint] = drawing.points;
    if (!startPoint || !endPoint || !offsetPoint) {
        return null;
    }
    const start = toPixel(startPoint, scales);
    const end = toPixel(endPoint, scales);
    const offsetAnchor = toPixel(offsetPoint, scales);
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const length = Math.hypot(dx, dy);
    const offsetVector = length === 0
        ? { x: offsetAnchor.x - start.x, y: offsetAnchor.y - start.y }
        : (() => {
            const normal = { x: -dy / length, y: dx / length };
            const distance = (offsetAnchor.x - start.x) * normal.x + (offsetAnchor.y - start.y) * normal.y;
            return { x: normal.x * distance, y: normal.y * distance };
        })();
    const offsetStart = { x: start.x + offsetVector.x, y: start.y + offsetVector.y };
    const offsetEnd = { x: end.x + offsetVector.x, y: end.y + offsetVector.y };
    const segments = [];
    if (isSegmentVisible(drawing, "primary")) {
        segments.push(toPrimitiveSegment(drawing, "primary", start, end));
    }
    if (isSegmentVisible(drawing, "secondary")) {
        segments.push(toPrimitiveSegment(drawing, "secondary", offsetStart, offsetEnd));
    }
    const labels = [];
    if (drawing.showMeasurement) {
        const percent = startPoint.y === 0 ? 0 : ((endPoint.y - startPoint.y) / Math.abs(startPoint.y)) * 100;
        const priceDiff = endPoint.y - startPoint.y;
        const bars = Math.max(1, Math.round(Math.abs(end.x - start.x) / 6));
        labels.push({
            text: `Δ ${priceDiff >= 0 ? "+" : ""}${priceDiff.toFixed(2)} (${percent >= 0 ? "+" : ""}${percent.toFixed(2)}%) · ${bars} bars`,
            position: { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 - 10 },
            align: "center",
            baseline: "alphabetic",
            weight: 700,
            fontSize: drawing.style.fontSize ?? 11,
        });
    }
    return {
        type: "channel",
        points: [start, end, offsetAnchor],
        style: primitiveStyle,
        segments,
        labels,
    };
}
function buildParallelChannelPrimitive(drawing, scales, primitiveStyle, options) {
    const geometry = calculateParallelChannelGeometry(drawing, scales);
    if (!geometry || options?.chartWidth == null || options?.chartHeight == null) {
        return null;
    }
    const segments = [];
    const upper = clipSegmentToBox({ x: geometry.start.x + geometry.offsetVector.x, y: geometry.start.y + geometry.offsetVector.y }, { x: geometry.end.x + geometry.offsetVector.x, y: geometry.end.y + geometry.offsetVector.y }, options.chartWidth, options.chartHeight);
    const middle = clipSegmentToBox(geometry.start, geometry.end, options.chartWidth, options.chartHeight);
    const lower = clipSegmentToBox({ x: geometry.start.x - geometry.offsetVector.x, y: geometry.start.y - geometry.offsetVector.y }, { x: geometry.end.x - geometry.offsetVector.x, y: geometry.end.y - geometry.offsetVector.y }, options.chartWidth, options.chartHeight);
    if (upper && isSegmentVisible(drawing, "upper")) {
        segments.push(toPrimitiveSegment(drawing, "upper", upper[0], upper[1]));
    }
    if (middle && isSegmentVisible(drawing, "middle")) {
        segments.push(toPrimitiveSegment(drawing, "middle", middle[0], middle[1]));
    }
    if (lower && isSegmentVisible(drawing, "lower")) {
        segments.push(toPrimitiveSegment(drawing, "lower", lower[0], lower[1]));
    }
    return {
        type: "parallelChannel",
        points: [geometry.start, geometry.end, geometry.offsetAnchor],
        style: primitiveStyle,
        segments,
    };
}
function buildPitchforkPrimitive(drawing, scales, primitiveStyle, options) {
    const geometry = calculatePitchforkGeometry(drawing, scales);
    if (!geometry || options?.chartWidth == null || options?.chartHeight == null || (geometry.direction.x === 0 && geometry.direction.y === 0)) {
        return null;
    }
    const median = extendLineThroughBox(geometry.pivot, geometry.direction, options.chartWidth, options.chartHeight);
    const leftFork = extendLineThroughBox(geometry.leftSwing, geometry.direction, options.chartWidth, options.chartHeight);
    const rightFork = extendLineThroughBox(geometry.rightSwing, geometry.direction, options.chartWidth, options.chartHeight);
    if (!median || !leftFork || !rightFork) {
        return null;
    }
    const segments = [];
    if (isSegmentVisible(drawing, "median")) {
        segments.push(toPrimitiveSegment(drawing, "median", median[0], median[1]));
    }
    if (isSegmentVisible(drawing, "leftFork")) {
        segments.push(toPrimitiveSegment(drawing, "leftFork", leftFork[0], leftFork[1]));
    }
    if (isSegmentVisible(drawing, "rightFork")) {
        segments.push(toPrimitiveSegment(drawing, "rightFork", rightFork[0], rightFork[1]));
    }
    return {
        type: "pitchfork",
        points: [geometry.pivot, geometry.leftSwing, geometry.rightSwing],
        style: primitiveStyle,
        segments,
        labels: [
            { text: "A", position: { x: geometry.pivot.x + 6, y: geometry.pivot.y - 6 }, baseline: "alphabetic", weight: 700, fontSize: drawing.style.fontSize ?? 11 },
            { text: "B", position: { x: geometry.leftSwing.x + 6, y: geometry.leftSwing.y - 6 }, baseline: "alphabetic", weight: 700, fontSize: drawing.style.fontSize ?? 11 },
            { text: "C", position: { x: geometry.rightSwing.x + 6, y: geometry.rightSwing.y - 6 }, baseline: "alphabetic", weight: 700, fontSize: drawing.style.fontSize ?? 11 },
        ],
    };
}
function buildRegressionChannelPrimitive(drawing, scales, primitiveStyle, options) {
    const metrics = calculateRegressionChannelMetrics(drawing, scales, options?.plotData ?? []);
    if (!metrics) {
        return null;
    }
    const fill = drawing.style.fill && drawing.style.fill !== "transparent" ? drawing.style.fill : "rgba(100,149,237,0.08)";
    const fillAlpha = (drawing.style.fillOpacity ?? 1) * (drawing.style.opacity ?? 1);
    const segments = [];
    if (isSegmentVisible(drawing, "upper")) {
        segments.push(toPrimitiveSegment(drawing, "upper", { x: metrics.startX, y: metrics.upperStartY }, { x: metrics.endX, y: metrics.upperEndY }, { strokeDasharray: "dashed" }));
    }
    if (isSegmentVisible(drawing, "lower")) {
        segments.push(toPrimitiveSegment(drawing, "lower", { x: metrics.startX, y: metrics.lowerStartY }, { x: metrics.endX, y: metrics.lowerEndY }, { strokeDasharray: "dashed" }));
    }
    if (isSegmentVisible(drawing, "center")) {
        segments.push(toPrimitiveSegment(drawing, "center", { x: metrics.startX, y: metrics.startY }, { x: metrics.endX, y: metrics.endY }));
    }
    return {
        type: "regressionChannel",
        points: drawing.points.slice(0, 2).map((point) => toPixel(point, scales)),
        style: primitiveStyle,
        polygons: [{
                points: [
                    { x: metrics.startX, y: metrics.upperStartY },
                    { x: metrics.endX, y: metrics.upperEndY },
                    { x: metrics.endX, y: metrics.lowerEndY },
                    { x: metrics.startX, y: metrics.lowerStartY },
                ],
                fill,
                opacity: fillAlpha,
            }],
        segments,
        labels: [{
                text: `R² ${metrics.rSquared.toFixed(2)}`,
                position: { x: metrics.endX - 4, y: Math.min(metrics.upperEndY, metrics.lowerEndY, metrics.endY) - 6 },
                align: "right",
                baseline: "alphabetic",
                weight: 700,
                fontSize: drawing.style.fontSize ?? 11,
            }],
    };
}
function isGhostCandle(v) {
    if (typeof v !== "object" || v === null)
        return false;
    const c = v;
    return [c.time, c.open, c.high, c.low, c.close].every((n) => typeof n === "number" && Number.isFinite(n));
}
/** Validated `(drawing as ghost).ghostData` — undefined when absent/invalid (never throws). */
export function readGhostData(drawing) {
    const raw = drawing.ghostData;
    if (!Array.isArray(raw) || raw.length === 0)
        return undefined;
    const valid = raw.filter(isGhostCandle);
    if (valid.length === 0 && raw.length > 0 && process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[drawing] ghostFeed ghostData invalid, falling back to plotData");
    }
    return valid.length > 0 ? valid : undefined;
}
function isPatternMatch(v) {
    if (typeof v !== "object" || v === null)
        return false;
    const m = v;
    return [m.index, m.similarity, m.time].every((n) => typeof n === "number" && Number.isFinite(n));
}
/** Validated `(drawing as pattern).patternMatches` — undefined when absent/invalid. */
export function readPatternMatches(drawing) {
    const raw = drawing.patternMatches;
    if (!Array.isArray(raw) || raw.length === 0)
        return undefined;
    const valid = raw.filter(isPatternMatch);
    return valid.length > 0 ? valid : undefined;
}
function toOverlayPrimitiveRaw(drawing, scales, options) {
    // Extension point (open-core split): Pro modules register builders for new types.
    const registered = _primitiveBuilders.get(drawing.type);
    if (registered) {
        return registered(drawing, scales, options);
    }
    const primitiveStyle = toPrimitiveStyle(drawing.style);
    switch (drawing.type) {
        case "trendLine":
        case "arrow":
        case "ray":
        case "extendedLine": {
            const [startPoint, endPoint] = drawing.points;
            if (!startPoint || !endPoint) {
                return null;
            }
            return {
                type: drawing.type,
                points: [toPixel(startPoint, scales), toPixel(endPoint, scales)],
                style: primitiveStyle,
            };
        }
        case "hLine": {
            const [point] = drawing.points;
            if (!point) {
                return null;
            }
            return {
                type: "hLine",
                points: [toPixel(point, scales)],
                style: primitiveStyle,
            };
        }
        case "vLine": {
            const [point] = drawing.points;
            if (!point) {
                return null;
            }
            return {
                type: "vLine",
                points: [toPixel(point, scales)],
                style: primitiveStyle,
            };
        }
        case "rectangle": {
            const [startPoint, endPoint] = drawing.points;
            if (!startPoint || !endPoint) {
                return null;
            }
            return {
                type: "rectangle",
                points: [toPixel(startPoint, scales), toPixel(endPoint, scales)],
                style: primitiveStyle,
            };
        }
        case "dateAndPriceRange": {
            const [startPoint, endPoint] = drawing.points;
            if (!startPoint || !endPoint) {
                return null;
            }
            return {
                type: "dateAndPriceRange",
                points: [toPixel(startPoint, scales), toPixel(endPoint, scales)],
                style: primitiveStyle,
                text: buildDateAndPriceRangeBadge(drawing, scales),
            };
        }
        case "fibonacci": {
            const [startPoint, endPoint] = drawing.points;
            if (!startPoint || !endPoint) {
                return null;
            }
            return {
                type: "fibonacci",
                points: [toPixel(startPoint, scales), toPixel(endPoint, scales)],
                style: primitiveStyle,
                levelLines: buildFibonacciLevelLines(drawing, scales, "retrace"),
            };
        }
        case "fibExtension": {
            const [startPoint, endPoint] = drawing.points;
            if (!startPoint || !endPoint) {
                return null;
            }
            return {
                type: "fibExtension",
                points: [toPixel(startPoint, scales), toPixel(endPoint, scales)],
                style: primitiveStyle,
                levelLines: buildFibonacciLevelLines(drawing, scales, "extension"),
            };
        }
        case "fibArc": {
            const geometry = calculateFibArcGeometry(drawing, scales);
            if (!geometry) {
                return null;
            }
            return {
                type: "fibArc",
                points: [geometry.center, geometry.reference],
                style: primitiveStyle,
                arcLevels: buildFibArcLevels(drawing, scales),
            };
        }
        case "fibTimeZone": {
            const geometry = calculateFibTimeZoneGeometry(drawing, scales);
            if (!geometry) {
                return null;
            }
            return {
                type: "fibTimeZone",
                points: [geometry.start, geometry.end],
                style: primitiveStyle,
                verticalMarkers: buildFibTimeZoneMarkers(drawing, scales),
            };
        }
        case "fibCircles":
        case "fibSpeedResistanceArcs":
        case "fibSpiral":
        case "fibSpeedResistanceFan":
        case "fibChannel":
        case "trendBasedFibTime":
        case "fibWedge":
        case "pitchfan":
            return buildFibExtendedPrimitive(drawing, scales, primitiveStyle);
        case "channel":
            return buildChannelPrimitive(drawing, scales, primitiveStyle);
        case "parallelChannel":
            return buildParallelChannelPrimitive(drawing, scales, primitiveStyle, options);
        case "pitchfork":
            return buildPitchforkPrimitive(drawing, scales, primitiveStyle, options);
        case "regressionChannel":
            return buildRegressionChannelPrimitive(drawing, scales, primitiveStyle, options);
        case "abcdPattern":
            return buildAbcdPatternPrimitive(drawing, scales, primitiveStyle);
        case "xabcdPattern":
        case "cypherPattern":
        case "headAndShoulders":
        case "trianglePattern":
        case "threeDrivesPattern":
            return buildHarmonicPatternPrimitive(drawing, scales, primitiveStyle, options);
        case "elliottImpulse":
        case "elliottCorrection":
        case "elliottTriangle":
        case "elliottDoubleCombo":
        case "elliottTripleCombo":
            return buildElliottWavePrimitive(drawing, scales, primitiveStyle, options);
        case "gannFan":
            return buildGannFanPrimitive(drawing, scales, primitiveStyle, options);
        case "gannBox":
        case "gannSquareFixed":
        case "gannSquare":
            return buildGannAdvancedPrimitive(drawing, scales, primitiveStyle);
        case "longPosition":
            return buildPositionPrimitive(drawing, scales, primitiveStyle, "long");
        case "shortPosition":
            return buildPositionPrimitive(drawing, scales, primitiveStyle, "short");
        case "cyclicLines":
        case "timeCycles":
        case "sineLine":
            return buildCyclePrimitive(drawing, scales, primitiveStyle);
        case "circle":
        case "ellipse": {
            const [startPoint, endPoint] = drawing.points;
            if (!startPoint || !endPoint) {
                return null;
            }
            return {
                type: drawing.type,
                points: [toPixel(startPoint, scales), toPixel(endPoint, scales)],
                style: primitiveStyle,
            };
        }
        case "triangle": {
            if (drawing.points.length < 3) {
                return null;
            }
            return {
                type: "triangle",
                points: drawing.points.slice(0, 3).map((point) => toPixel(point, scales)),
                style: primitiveStyle,
            };
        }
        case "callout":
        case "priceLabel":
        case "text": {
            const ann = buildAnnotationPrimitive(drawing, scales, primitiveStyle, toPixel);
            if (ann)
                return ann;
            return null;
        }
        case "priceRange":
        case "dateRange":
        case "barsCount": {
            const [startPoint, endPoint] = drawing.points;
            if (!startPoint || !endPoint) {
                return null;
            }
            return {
                type: drawing.type,
                points: [toPixel(startPoint, scales), toPixel(endPoint, scales)],
                style: primitiveStyle,
                text: drawing.text,
            };
        }
        case "polyline": {
            if (drawing.points.length < 2) {
                return null;
            }
            return {
                type: "polyline",
                points: drawing.points.map((point) => toPixel(point, scales)),
                style: primitiveStyle,
            };
        }
        case "disjointChannel": {
            const geometry = calculateDisjointChannelGeometry(drawing, scales);
            if (!geometry) {
                return null;
            }
            const segments = [];
            if (isSegmentVisible(drawing, "upperLine")) {
                segments.push(toPrimitiveSegment(drawing, "upperLine", geometry.upperLine.start, geometry.upperLine.end));
            }
            if (isSegmentVisible(drawing, "lowerLine")) {
                segments.push(toPrimitiveSegment(drawing, "lowerLine", geometry.lowerLine.start, geometry.lowerLine.end));
            }
            return {
                type: "disjointChannel",
                points: drawing.points.map((p) => toPixel(p, scales)),
                style: primitiveStyle,
                segments,
            };
        }
        case "flatTopBottom": {
            const geometry = calculateFlatTopBottomGeometry(drawing, scales);
            if (!geometry) {
                return null;
            }
            const segments = [];
            if (isSegmentVisible(drawing, "line")) {
                segments.push(toPrimitiveSegment(drawing, "line", { x: geometry.xStart, y: geometry.y }, { x: geometry.xEnd, y: geometry.y }));
            }
            return {
                type: "flatTopBottom",
                points: drawing.points.map((p) => toPixel(p, scales)),
                style: primitiveStyle,
                segments,
            };
        }
        case "schiffPitchfork": {
            const geometry = calculateSchiffPitchforkGeometry(drawing, scales);
            if (!geometry) {
                return null;
            }
            const segments = [];
            if (isSegmentVisible(drawing, "median")) {
                segments.push(toPrimitiveSegment(drawing, "median", geometry.medianLine.start, geometry.medianLine.end));
            }
            if (isSegmentVisible(drawing, "leftFork")) {
                segments.push(toPrimitiveSegment(drawing, "leftFork", geometry.upperLine.start, geometry.upperLine.end));
            }
            if (isSegmentVisible(drawing, "rightFork")) {
                segments.push(toPrimitiveSegment(drawing, "rightFork", geometry.lowerLine.start, geometry.lowerLine.end));
            }
            return {
                type: "schiffPitchfork",
                points: [geometry.pivot, geometry.leftSwing, geometry.rightSwing],
                style: primitiveStyle,
                segments,
                labels: [
                    { text: "A", position: { x: geometry.pivot.x + 6, y: geometry.pivot.y - 6 }, align: "left", baseline: "alphabetic", weight: 700, fontSize: drawing.style.fontSize ?? 11 },
                    { text: "B", position: { x: geometry.leftSwing.x + 6, y: geometry.leftSwing.y - 6 }, align: "left", baseline: "alphabetic", weight: 700, fontSize: drawing.style.fontSize ?? 11 },
                    { text: "C", position: { x: geometry.rightSwing.x + 6, y: geometry.rightSwing.y - 6 }, align: "left", baseline: "alphabetic", weight: 700, fontSize: drawing.style.fontSize ?? 11 },
                ],
            };
        }
        case "modifiedSchiff": {
            const geometry = calculateModifiedSchiffGeometry(drawing, scales);
            if (!geometry) {
                return null;
            }
            const segments = [];
            if (isSegmentVisible(drawing, "median")) {
                segments.push(toPrimitiveSegment(drawing, "median", geometry.medianLine.start, geometry.medianLine.end));
            }
            if (isSegmentVisible(drawing, "leftFork")) {
                segments.push(toPrimitiveSegment(drawing, "leftFork", geometry.upperLine.start, geometry.upperLine.end));
            }
            if (isSegmentVisible(drawing, "rightFork")) {
                segments.push(toPrimitiveSegment(drawing, "rightFork", geometry.lowerLine.start, geometry.lowerLine.end));
            }
            return {
                type: "modifiedSchiff",
                points: [geometry.pivot, geometry.leftSwing, geometry.rightSwing],
                style: primitiveStyle,
                segments,
                labels: [
                    { text: "A", position: { x: geometry.pivot.x + 6, y: geometry.pivot.y - 6 }, align: "left", baseline: "alphabetic", weight: 700, fontSize: drawing.style.fontSize ?? 11 },
                    { text: "B", position: { x: geometry.leftSwing.x + 6, y: geometry.leftSwing.y - 6 }, align: "left", baseline: "alphabetic", weight: 700, fontSize: drawing.style.fontSize ?? 11 },
                    { text: "C", position: { x: geometry.rightSwing.x + 6, y: geometry.rightSwing.y - 6 }, align: "left", baseline: "alphabetic", weight: 700, fontSize: drawing.style.fontSize ?? 11 },
                ],
            };
        }
        case "insidePitchfork": {
            const geometry = calculateInsidePitchforkGeometry(drawing, scales);
            if (!geometry) {
                return null;
            }
            const segments = [];
            if (isSegmentVisible(drawing, "median")) {
                segments.push(toPrimitiveSegment(drawing, "median", geometry.medianLine.start, geometry.medianLine.end));
            }
            if (isSegmentVisible(drawing, "leftFork")) {
                segments.push(toPrimitiveSegment(drawing, "leftFork", geometry.upperLine.start, geometry.upperLine.end));
            }
            if (isSegmentVisible(drawing, "rightFork")) {
                segments.push(toPrimitiveSegment(drawing, "rightFork", geometry.lowerLine.start, geometry.lowerLine.end));
            }
            return {
                type: "insidePitchfork",
                points: [geometry.pivot, geometry.leftSwing, geometry.rightSwing],
                style: primitiveStyle,
                segments,
                labels: [
                    { text: "A", position: { x: geometry.pivot.x + 6, y: geometry.pivot.y - 6 }, align: "left", baseline: "alphabetic", weight: 700, fontSize: drawing.style.fontSize ?? 11 },
                    { text: "B", position: { x: geometry.leftSwing.x + 6, y: geometry.leftSwing.y - 6 }, align: "left", baseline: "alphabetic", weight: 700, fontSize: drawing.style.fontSize ?? 11 },
                    { text: "C", position: { x: geometry.rightSwing.x + 6, y: geometry.rightSwing.y - 6 }, align: "left", baseline: "alphabetic", weight: 700, fontSize: drawing.style.fontSize ?? 11 },
                ],
            };
        }
        case "gannFixed": {
            const geometry = calculateGannFixedGeometry(drawing, scales);
            if (!geometry) {
                return null;
            }
            const segments = [];
            const labels = [];
            for (const line of geometry.lines) {
                if (isSegmentVisible(drawing, line.angle)) {
                    segments.push(toPrimitiveSegment(drawing, line.angle, line.start, line.end));
                    labels.push({
                        text: line.angle,
                        position: { x: line.end.x - 10, y: line.end.y - 5 },
                        align: "center",
                        baseline: "alphabetic",
                        weight: 700,
                        fontSize: drawing.style.fontSize ?? 9,
                    });
                }
            }
            return {
                type: "gannFixed",
                points: [geometry.pivot],
                style: primitiveStyle,
                segments,
                labels,
            };
        }
        case "trendAngle": {
            const geometry = calculateTrendAngleGeometry(drawing, scales);
            if (!geometry) {
                return null;
            }
            const segments = [];
            if (isSegmentVisible(drawing, "line")) {
                segments.push(toPrimitiveSegment(drawing, "line", geometry.start, geometry.end));
            }
            const labels = [{
                    text: `${geometry.angleDegrees.toFixed(1)}°`,
                    position: { x: geometry.end.x + 10, y: geometry.end.y - 5 },
                    align: "left",
                    baseline: "alphabetic",
                    weight: 700,
                    fontSize: drawing.style.fontSize ?? 11,
                }];
            return {
                type: "trendAngle",
                points: [geometry.start, geometry.end],
                style: primitiveStyle,
                segments,
                labels,
            };
        }
        case "infoLine": {
            const geometry = calculateInfoLineGeometry(drawing, scales);
            if (!geometry) {
                return null;
            }
            const segments = [];
            if (isSegmentVisible(drawing, "line")) {
                segments.push(toPrimitiveSegment(drawing, "line", geometry.start, geometry.end));
            }
            const sign = geometry.priceChange >= 0 ? "+" : "";
            const text = `Δ ${sign}${geometry.priceChange.toFixed(2)} (${sign}${geometry.priceChangePercent.toFixed(2)}%) · ${geometry.barsCount} bars`;
            const labels = [{
                    text,
                    position: { x: (geometry.start.x + geometry.end.x) / 2, y: (geometry.start.y + geometry.end.y) / 2 - 10 },
                    align: "center",
                    baseline: "alphabetic",
                    weight: 700,
                    fontSize: drawing.style.fontSize ?? 11,
                }];
            return {
                type: "infoLine",
                points: [geometry.start, geometry.end],
                style: primitiveStyle,
                segments,
                labels,
            };
        }
        case "brush":
        case "highlighter":
        case "eraser": {
            if (drawing.points.length < 2) {
                return null;
            }
            const pxPoints = drawing.points.map((p) => toPixel(p, scales));
            // S3.2: engine draws brush/highlighter/eraser identically
            // (canvasOverlayRenderer drawOverlayPaths) but the primitive-type
            // union only lists "brush" — map explicitly instead of an untyped cast.
            return {
                type: "brush",
                points: pxPoints,
                style: primitiveStyle,
                paths: [{
                        points: pxPoints,
                    }],
            };
        }
        case "curve": {
            if (drawing.points.length < 3) {
                return null;
            }
            const p0 = toPixel(drawing.points[0], scales);
            const p1 = toPixel(drawing.points[1], scales);
            const p2 = toPixel(drawing.points[2], scales);
            const pathPoints = [];
            for (let i = 0; i <= 30; i++) {
                const t = i / 30;
                const mt = 1 - t;
                const x = mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x;
                const y = mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y;
                pathPoints.push({ x, y });
            }
            return {
                type: "curve",
                points: [p0, p1, p2],
                style: primitiveStyle,
                paths: [{
                        points: pathPoints,
                    }],
            };
        }
        case "arc": {
            if (drawing.points.length < 3) {
                return null;
            }
            const p0 = toPixel(drawing.points[0], scales);
            const p1 = toPixel(drawing.points[1], scales);
            const p2 = toPixel(drawing.points[2], scales);
            const d = 2 * (p0.x * (p1.y - p2.y) + p1.x * (p2.y - p0.y) + p2.x * (p0.y - p1.y));
            let pathPoints = [];
            if (Math.abs(d) < 1e-6) {
                pathPoints = [p0, p2];
            }
            else {
                const ux = ((p0.x * p0.x + p0.y * p0.y) * (p1.y - p2.y) + (p1.x * p1.x + p1.y * p1.y) * (p2.y - p0.y) + (p2.x * p2.x + p2.y * p2.y) * (p0.y - p1.y)) / d;
                const uy = ((p0.x * p0.x + p0.y * p0.y) * (p2.x - p1.x) + (p1.x * p1.x + p1.y * p1.y) * (p0.x - p2.x) + (p2.x * p2.x + p2.y * p2.y) * (p1.x - p0.x)) / d;
                const radius = Math.hypot(p0.x - ux, p0.y - uy);
                const a0 = Math.atan2(p0.y - uy, p0.x - ux);
                const a1 = Math.atan2(p1.y - uy, p1.x - ux);
                const a2 = Math.atan2(p2.y - uy, p2.x - ux);
                const diffAngle = (target, source) => {
                    let diff = target - source;
                    while (diff < -Math.PI)
                        diff += 2 * Math.PI;
                    while (diff > Math.PI)
                        diff -= 2 * Math.PI;
                    return diff;
                };
                const dir = Math.sign(diffAngle(a1, a0));
                let sweep = diffAngle(a2, a0);
                if (Math.sign(sweep) !== dir) {
                    sweep = sweep > 0 ? sweep - 2 * Math.PI : sweep + 2 * Math.PI;
                }
                for (let i = 0; i <= 30; i++) {
                    const angle = a0 + sweep * (i / 30);
                    pathPoints.push({ x: ux + radius * Math.cos(angle), y: uy + radius * Math.sin(angle) });
                }
            }
            return {
                type: "arc",
                points: [p0, p1, p2],
                style: primitiveStyle,
                paths: [{
                        points: pathPoints,
                    }],
            };
        }
        case "path": {
            if (drawing.points.length < 1) {
                return null;
            }
            const pxPoints = drawing.points.map((p) => toPixel(p, scales));
            return {
                type: "path",
                points: pxPoints,
                style: primitiveStyle,
                paths: [{
                        points: pxPoints,
                    }],
            };
        }
        case "polygon": {
            if (drawing.points.length < 3) {
                const pxPoints = drawing.points.map((p) => toPixel(p, scales));
                const segments = [];
                if (pxPoints.length >= 2) {
                    segments.push(toPrimitiveSegment(drawing, "border0", pxPoints[0], pxPoints[1]));
                }
                return {
                    type: "polygon",
                    points: pxPoints,
                    style: primitiveStyle,
                    segments,
                    polygons: [],
                };
            }
            const pxPoints = drawing.points.map((p) => toPixel(p, scales));
            const segments = [];
            for (let i = 0; i < pxPoints.length; i++) {
                const start = pxPoints[i];
                const end = pxPoints[(i + 1) % pxPoints.length];
                segments.push(toPrimitiveSegment(drawing, `border${i}`, start, end));
            }
            return {
                type: "polygon",
                points: pxPoints,
                style: primitiveStyle,
                segments,
                polygons: [{
                        points: pxPoints,
                        fill: drawing.style.fill ?? "rgba(0,0,0,0.1)",
                        opacity: (drawing.style.fillOpacity ?? 1) * (drawing.style.opacity ?? 1),
                    }],
            };
        }
        case "rotatedRectangle": {
            const [p0, p1] = drawing.points;
            if (!p0 || !p1) {
                return null;
            }
            const px0 = toPixel(p0, scales);
            const px1 = toPixel(p1, scales);
            const corners = [
                { x: px0.x, y: px0.y },
                { x: px1.x, y: px0.y },
                { x: px1.x, y: px1.y },
                { x: px0.x, y: px1.y },
            ];
            const segments = [
                toPrimitiveSegment(drawing, "border1", corners[0], corners[1]),
                toPrimitiveSegment(drawing, "border2", corners[1], corners[2]),
                toPrimitiveSegment(drawing, "border3", corners[2], corners[3]),
                toPrimitiveSegment(drawing, "border4", corners[3], corners[0]),
            ];
            return {
                type: "rotatedRectangle",
                points: [px0, px1],
                style: primitiveStyle,
                segments,
                polygons: [{
                        points: corners,
                        fill: drawing.style.fill ?? "rgba(0,0,0,0.1)",
                        opacity: (drawing.style.fillOpacity ?? 1) * (drawing.style.opacity ?? 1),
                    }],
            };
        }
        // CE24: Drawing Tools Pack 3
        case "anchoredVwap": {
            // S3.2: explicit candle mapping (PlotDatum → geometry input) with
            // finite guards instead of an untyped cast.
            const candles = (options?.plotData ?? []).map((d) => ({
                time: typeof d.date === "number" ? d.date : d.date.getTime(),
                date: d.date,
                open: d.open,
                high: d.high,
                low: d.low,
                close: d.close,
                volume: d.volume,
            })).filter((c) => Number.isFinite(c.time) && [c.open, c.high, c.low, c.close, c.volume].every(Number.isFinite));
            const geometry = calculateAnchoredVwapGeometry(drawing, scales, candles);
            if (!geometry)
                return null;
            const paths = [];
            if (geometry.vwapLine.length > 0) {
                paths.push({
                    points: geometry.vwapLine,
                    color: drawing.style.stroke ?? "#FF6D00",
                    strokeWidth: drawing.style.strokeWidth ?? 2,
                });
            }
            if (geometry.upperBand.length > 0) {
                paths.push({
                    points: geometry.upperBand,
                    color: drawing.style.stroke ?? "#FF6D00",
                    strokeWidth: 1,
                    strokeDasharray: "dashed",
                });
            }
            if (geometry.lowerBand.length > 0) {
                paths.push({
                    points: geometry.lowerBand,
                    color: drawing.style.stroke ?? "#FF6D00",
                    strokeWidth: 1,
                    strokeDasharray: "dashed",
                });
            }
            return {
                type: "anchoredVwap",
                points: [geometry.anchor],
                style: primitiveStyle,
                paths,
            };
        }
        case "iconMarker": {
            const point = drawing.points[0];
            if (!point)
                return null;
            const px = toPixel(point, scales);
            // Use custom icon from style or default to pin emoji
            const icon = drawing.style.icon ?? "📍";
            const iconSize = drawing.style.size ?? drawing.style.fontSize ?? 24;
            const iconColor = drawing.style.color ?? drawing.style.stroke ?? "#FFFFFF";
            return {
                type: "iconMarker",
                points: [px],
                style: primitiveStyle,
                labels: [{
                        text: icon,
                        position: px,
                        color: iconColor,
                        fontSize: iconSize,
                        align: "center",
                        baseline: "middle",
                    }],
            };
        }
        case "ghostFeed": {
            const anchor = drawing.points[0];
            if (!anchor)
                return null;
            const px = toPixel(anchor, scales);
            const plotData = options?.plotData ?? [];
            const anchorTime = anchor.timeMsHint ?? anchor.x;
            let anchorIndex = plotData.findIndex(d => (typeof d.date === 'number' ? d.date : d.date.getTime()) >= anchorTime);
            if (anchorIndex === -1)
                anchorIndex = 0;
            const candlesSource = readGhostData(drawing);
            const ghostStyle = drawing.style;
            const opacity = ghostStyle.opacity ?? 0.4;
            const colorUp = "#00E676";
            const colorDown = "#FF1744";
            const segments = [];
            const polygons = [];
            let barWidth = 6;
            if (plotData.length >= 2) {
                const x0 = toPixel({ x: typeof plotData[0].date === "number" ? plotData[0].date : plotData[0].date.getTime(), y: 0 }, scales).x;
                const x1 = toPixel({ x: typeof plotData[1].date === "number" ? plotData[1].date : plotData[1].date.getTime(), y: 0 }, scales).x;
                barWidth = Math.max(2, Math.abs(x1 - x0) * 0.7);
            }
            const halfWidth = barWidth / 2;
            const listToRender = candlesSource && candlesSource.length > 0
                ? candlesSource
                : plotData.slice(anchorIndex, anchorIndex + 10).map((bar) => ({
                    time: typeof bar.date === "number" ? bar.date : bar.date.getTime(),
                    open: bar.open,
                    high: bar.high,
                    low: bar.low,
                    close: bar.close,
                }));
            for (let i = 0; i < listToRender.length; i++) {
                const item = listToRender[i];
                const isUp = item.close >= item.open;
                const candleColor = isUp ? colorUp : colorDown;
                const targetIndex = anchorIndex + i;
                let pxX = px.x;
                if (targetIndex < plotData.length) {
                    const targetBar = plotData[targetIndex];
                    pxX = toPixel({ x: typeof targetBar.date === "number" ? targetBar.date : targetBar.date.getTime(), y: 0 }, scales).x;
                }
                else {
                    pxX = px.x + i * (barWidth / 0.7);
                }
                const yOpen = scales.yScale(item.open);
                const yClose = scales.yScale(item.close);
                const yHigh = scales.yScale(item.high);
                const yLow = scales.yScale(item.low);
                segments.push({
                    start: { x: pxX, y: yHigh },
                    end: { x: pxX, y: yLow },
                    color: candleColor,
                    strokeWidth: 1,
                    opacity,
                });
                const topY = Math.min(yOpen, yClose);
                const bottomY = Math.max(yOpen, yClose);
                polygons.push({
                    points: [
                        { x: pxX - halfWidth, y: topY },
                        { x: pxX + halfWidth, y: topY },
                        { x: pxX + halfWidth, y: bottomY },
                        { x: pxX - halfWidth, y: bottomY },
                    ],
                    fill: candleColor,
                    opacity,
                });
            }
            return {
                type: "ghostFeed",
                points: [px],
                style: primitiveStyle,
                segments,
                polygons,
            };
        }
        case "barsPattern": {
            if (drawing.points.length < 2)
                return null;
            const pxPoints = drawing.points.map(p => toPixel(p, scales));
            // Get pattern match data from drawing (validated bridge reader).
            const patternMatches = readPatternMatches(drawing);
            const paths = [];
            const pointMarkers = [];
            // Render the base pattern (first two points define the pattern)
            if (pxPoints.length >= 2) {
                paths.push({
                    points: pxPoints.slice(0, 2),
                    color: drawing.style.stroke ?? "#00E676",
                    strokeWidth: drawing.style.strokeWidth ?? 2,
                });
            }
            // Render matched patterns with similarity-based opacity
            if (patternMatches && patternMatches.length > 0) {
                const plotData = options?.plotData ?? [];
                for (const match of patternMatches) {
                    const matchIndex = plotData.findIndex(d => (typeof d.date === "number" ? d.date : d.date.getTime()) >= match.time);
                    if (matchIndex >= 0 && matchIndex + 2 <= plotData.length) {
                        const matchPoints = plotData.slice(matchIndex, matchIndex + 2).map(d => toPixel({ x: typeof d.date === "number" ? d.date : d.date.getTime(), y: d.close, timeMsHint: typeof d.date === "number" ? d.date : d.date.getTime(), yPrice: d.close }, scales));
                        paths.push({
                            points: matchPoints,
                            color: drawing.style.stroke ?? "#00E676",
                            strokeWidth: drawing.style.strokeWidth ?? 1,
                            opacity: 0.3 + 0.5 * match.similarity, // Higher similarity = more opaque
                        });
                    }
                }
            }
            return {
                type: "barsPattern",
                points: pxPoints,
                style: primitiveStyle,
                paths,
                pointMarkers,
            };
        }
        case "crossLine": {
            const point = drawing.points[0];
            if (!point)
                return null;
            const px = toPixel(point, scales);
            const chartWidth = options?.chartWidth ?? 800;
            const chartHeight = options?.chartHeight ?? 600;
            const showLabels = drawing.style.showLabels ?? true;
            const segments = [
                // Horizontal line
                {
                    start: { x: 0, y: px.y },
                    end: { x: chartWidth, y: px.y },
                    color: drawing.style.stroke ?? "#FFFFFF",
                    strokeWidth: drawing.style.strokeWidth ?? 1,
                    strokeDasharray: drawing.style.strokeDasharray ?? "dashed",
                    opacity: drawing.style.opacity ?? 0.5,
                    hitTestable: true,
                },
                // Vertical line
                {
                    start: { x: px.x, y: 0 },
                    end: { x: px.x, y: chartHeight },
                    color: drawing.style.stroke ?? "#FFFFFF",
                    strokeWidth: drawing.style.strokeWidth ?? 1,
                    strokeDasharray: drawing.style.strokeDasharray ?? "dashed",
                    opacity: drawing.style.opacity ?? 0.5,
                    hitTestable: true,
                },
            ];
            const labels = [];
            if (showLabels) {
                // Price label on horizontal line
                const price = point.yPrice ?? scales.yScaleInvert(px.y);
                if (price !== null && Number.isFinite(price)) {
                    labels.push({
                        text: price.toFixed(2),
                        position: { x: px.x + 8, y: px.y - 8 },
                        color: drawing.style.stroke ?? "#FFFFFF",
                        fontSize: drawing.style.fontSize ?? 11,
                        align: "left",
                        baseline: "bottom",
                    });
                }
                // Time label on vertical line
                const time = point.timeMsHint ?? point.x;
                const date = new Date(time);
                labels.push({
                    text: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    position: { x: px.x + 8, y: px.y + 8 },
                    color: drawing.style.stroke ?? "#FFFFFF",
                    fontSize: drawing.style.fontSize ?? 11,
                    align: "left",
                    baseline: "top",
                });
            }
            return {
                type: "crossLine",
                points: [px],
                style: primitiveStyle,
                segments,
                labels,
            };
        }
        case "datePriceRange": {
            if (drawing.points.length < 2)
                return null;
            const [p0, p1] = drawing.points;
            const px0 = toPixel(p0, scales);
            const px1 = toPixel(p1, scales);
            const chartWidth = options?.chartWidth ?? 800;
            const chartHeight = options?.chartHeight ?? 600;
            // Calculate stats
            const price0 = p0.yPrice ?? p0.y;
            const price1 = p1.yPrice ?? p1.y;
            const priceChange = price1 - price0;
            const priceChangePercent = price0 !== 0 ? (priceChange / price0) * 100 : 0;
            const barsCount = Math.abs((p1.xIndexFloat ?? 0) - (p0.xIndexFloat ?? 0));
            const timeDiff = Math.abs((p1.timeMsHint ?? p1.x) - (p0.timeMsHint ?? p0.x));
            const daysCount = timeDiff / (1000 * 60 * 60 * 24);
            const angle = Math.atan2(px1.y - px0.y, px1.x - px0.x) * 180 / Math.PI;
            const segments = [
                toPrimitiveSegment(drawing, "main", px0, px1),
                toPrimitiveSegment(drawing, "hLine0", { x: 0, y: px0.y }, { x: px0.x, y: px0.y }),
                toPrimitiveSegment(drawing, "hLine1", { x: px1.x, y: px1.y }, { x: chartWidth, y: px1.y }),
                toPrimitiveSegment(drawing, "vLine0", { x: px0.x, y: 0 }, { x: px0.x, y: px0.y }),
                toPrimitiveSegment(drawing, "vLine1", { x: px1.x, y: px1.y }, { x: px1.x, y: chartHeight }),
            ];
            // Stats label
            const midX = (px0.x + px1.x) / 2;
            const midY = (px0.y + px1.y) / 2;
            const statsText = `Δ${priceChange.toFixed(2)} (${priceChangePercent.toFixed(2)}%)
Bars: ${barsCount.toFixed(0)}
Days: ${daysCount.toFixed(1)}
Angle: ${angle.toFixed(1)}°`;
            const labels = [{
                    text: statsText,
                    position: { x: midX, y: midY },
                    color: drawing.style.stroke ?? "#00E676",
                    fontSize: drawing.style.fontSize ?? 11,
                    align: "center",
                    baseline: "middle",
                }];
            return {
                type: "datePriceRange",
                points: [px0, px1],
                style: primitiveStyle,
                segments,
                labels,
            };
        }
        default:
            if (process.env.NODE_ENV !== "production") {
                // eslint-disable-next-line no-console
                console.warn("[drawing] unhandled DrawingToolType in toOverlayPrimitiveRaw:", drawing.type);
            }
            return null;
    }
}
export function toOverlayPrimitive(drawing, scales, options) {
    const primitive = toOverlayPrimitiveRaw(drawing, scales, options);
    if (!primitive) {
        return null;
    }
    const enrichPoint = (point) => {
        if (typeof point.x === "number" && Number.isFinite(point.x) && point.xIndexFloat === undefined) {
            if (scales.xScaleInvertToIndex) {
                const idx = scales.xScaleInvertToIndex(point.x);
                if (idx !== null && Number.isFinite(idx)) {
                    point.xIndexFloat = idx;
                }
            }
        }
        if (typeof point.y === "number" && Number.isFinite(point.y) && point.yPrice === undefined) {
            const price = scales.yScaleInvert(point.y);
            if (price !== null && Number.isFinite(price)) {
                point.yPrice = price;
            }
        }
    };
    if (primitive.points) {
        primitive.points.forEach(enrichPoint);
    }
    if (primitive.segments) {
        primitive.segments = primitive.segments
            .map((seg) => {
            if (seg.start)
                enrichPoint(seg.start);
            if (seg.end)
                enrichPoint(seg.end);
            return seg;
        })
            .map((seg) => {
            const chartWidth = options?.chartWidth;
            const chartHeight = options?.chartHeight;
            if (!chartWidth || !chartHeight) {
                return seg;
            }
            // R-03/P1: chỉ cắt pixel, KHÔNG gán ngược anchor từ pixel mép.
            // Anchor chỉ giữ khi đầu mút nằm hẳn TRONG viewport (raw builders
            // đã cắt/kéo tới mép từ trước thì anchor đó là giả mạo). Đầu mút
            // trên mép hoặc bị cắt giữ pixel trần để Step-2 giữ nguyên.
            const clipped = clipSegmentToViewport(seg, chartWidth, chartHeight);
            if (!clipped) {
                return null;
            }
            const keep = (p) => p.x > 1e-9 && p.x < chartWidth - 1e-9 && p.y > 1e-9 && p.y < chartHeight - 1e-9;
            return {
                ...seg,
                start: keep(clipped.start) ? seg.start : clipped.start,
                end: keep(clipped.end) ? seg.end : clipped.end,
            };
        })
            .filter((s) => s !== null);
    }
    if (primitive.paths) {
        primitive.paths.forEach((path) => {
            if (path.points)
                path.points.forEach(enrichPoint);
        });
    }
    if (primitive.pointMarkers) {
        primitive.pointMarkers.forEach((pm) => {
            if (pm.point)
                enrichPoint(pm.point);
        });
    }
    // R-03 labels: positions carry baked-in pixel offsets (+6px/-10px) by design,
    // so they are intentionally NOT enriched into anchors (consumers read pixels).
    if (primitive.polygons) {
        primitive.polygons.forEach((poly) => {
            if (poly.points)
                poly.points.forEach(enrichPoint);
        });
    }
    if (primitive.semanticBadges) {
        primitive.semanticBadges.forEach((badge) => {
            if (badge.position)
                enrichPoint(badge.position);
        });
    }
    if (primitive.levelLines) {
        primitive.levelLines.forEach((lvl) => {
            if (lvl.yPrice === undefined && typeof lvl.y === "number" && Number.isFinite(lvl.y)) {
                const price = scales.yScaleInvert(lvl.y);
                if (price !== null && Number.isFinite(price)) {
                    lvl.yPrice = price;
                }
            }
        });
    }
    if (primitive.verticalMarkers) {
        primitive.verticalMarkers.forEach((vm) => {
            if (vm.xIndexFloat === undefined && typeof vm.x === "number" && Number.isFinite(vm.x)) {
                if (scales.xScaleInvertToIndex) {
                    const idx = scales.xScaleInvertToIndex(vm.x);
                    if (idx !== null && Number.isFinite(idx)) {
                        vm.xIndexFloat = idx;
                    }
                }
            }
        });
    }
    // R-03/P1: arc labelPositions are pure-pixel offsets (radius*0.707) by design,
    // so they are intentionally NOT enriched — Step-2 fallback recompute is correct.
    return primitive;
}
